/**
 * D3 Corner House Animator
 * 
 * Revolutionary solution for corner houses (0, 3, 6, 9) using sprite rotation
 * instead of complex SVG arc calculations. Eliminates need for:
 * - getTotalLength() / getPointAtLength()
 * - Temporary DOM element creation
 * - Epsilon-based tangent calculations
 */

import * as d3 from 'd3';
import { PlanetSprite, D3PlanetAnimator, AnimationConfig } from './D3PlanetAnimator';

export interface CornerHouseConfig {
  containerIndex: number;
  startAngle: number;    // Starting angle for the corner house arc
  endAngle: number;      // Ending angle for the corner house arc
  rotationOffset: number; // Base rotation offset for sprite orientation
}

export class D3CornerHouseAnimator {
  
  // Corner house configurations (matching North Indian layout)
  private static readonly CORNER_CONFIGS: Record<number, CornerHouseConfig> = {
    0: { containerIndex: 0, startAngle: -45, endAngle: 45, rotationOffset: 0 },    // 12th house (Meena/Pisces)
    3: { containerIndex: 3, startAngle: 45, endAngle: 135, rotationOffset: 90 },   // 3rd house (Mithuna/Gemini)
    6: { containerIndex: 6, startAngle: 135, endAngle: 225, rotationOffset: 180 }, // 6th house (Kanya/Virgo)
    9: { containerIndex: 9, startAngle: 225, endAngle: 315, rotationOffset: 270 }  // 9th house (Dhanu/Sagittarius)
  };

  /**
   * Check if a container is a corner house
   */
  public static isCornerHouse(containerIndex: number): boolean {
    return containerIndex in D3CornerHouseAnimator.CORNER_CONFIGS;
  }

  /**
   * Calculate sprite rotation for corner house based on longitude
   * Revolutionary approach: Simple mathematical calculation instead of SVG path computation
   */
  public static calculateCornerRotation(
    containerIndex: number,
    longitude: number
  ): number {
    
    const config = D3CornerHouseAnimator.CORNER_CONFIGS[containerIndex];
    if (!config) {
      return 0; // Not a corner house
    }

    // Calculate progress within the house (0 to 1)
    const degreesInHouse = longitude % 30;
    const progress = degreesInHouse / 30.0;
    
    // Calculate rotation based on corner house arc
    const angleRange = config.endAngle - config.startAngle;
    const currentAngle = config.startAngle + (progress * angleRange);
    
    // Apply rotation offset for proper sprite orientation
    return currentAngle + config.rotationOffset;
  }

  /**
   * Create D3 interpolator for smooth corner house transitions
   * Uses mathematical interpolation instead of SVG path interpolation
   */
  public static createCornerInterpolator(
    startPosition: { x: number, y: number },
    endPosition: { x: number, y: number },
    startLongitude: number,
    endLongitude: number,
    containerIndex: number
  ): (t: number) => { x: number, y: number, rotation: number } {
    
    if (!D3CornerHouseAnimator.isCornerHouse(containerIndex)) {
      // Fallback for non-corner houses
      const positionInterpolator = d3.interpolate(startPosition, endPosition);
      return (t: number) => ({
        ...positionInterpolator(t),
        rotation: 0
      });
    }

    // Create interpolators
    const positionInterpolator = d3.interpolate(startPosition, endPosition);
    const longitudeInterpolator = d3.interpolateNumber(startLongitude, endLongitude);
    
    return (t: number) => {
      const position = positionInterpolator(t);
      const currentLongitude = longitudeInterpolator(t);
      const rotation = D3CornerHouseAnimator.calculateCornerRotation(containerIndex, currentLongitude);
      
      return { ...position, rotation };
    };
  }

  /**
   * Animate planet along corner house arc using sprite rotation
   * Eliminates complex SVG path calculations
   */
  public static animateCornerHouseMovement(
    sprite: PlanetSprite,
    startPosition: { x: number, y: number },
    endPosition: { x: number, y: number },
    startLongitude: number,
    endLongitude: number,
    config: AnimationConfig = D3PlanetAnimator.ANIMATION_PRESETS.SMOOTH
  ): Promise<void> {
    
    const containerIndex = sprite.containerIndex;
    
    if (!D3CornerHouseAnimator.isCornerHouse(containerIndex)) {
      // Fallback to regular animation for non-corner houses
      return D3PlanetAnimator.animatePlanetSprite(sprite, endPosition, 0, config);
    }

    const interpolator = D3CornerHouseAnimator.createCornerInterpolator(
      startPosition,
      endPosition,
      startLongitude,
      endLongitude,
      containerIndex
    );

    return new Promise((resolve) => {
      sprite.container
        .transition()
        .duration(config.duration)
        .delay(config.delay || 0)
        .ease(config.easing)
        .tween('cornerMovement', () => {
          return (t: number) => {
            const { x, y, rotation } = interpolator(t);
            sprite.container.attr('transform', `translate(${x}, ${y}) rotate(${rotation})`);
          };
        })
        .on('end', () => {
          // Update sprite state
          const finalState = interpolator(1);
          sprite.position = { x: finalState.x, y: finalState.y };
          sprite.rotation = finalState.rotation;
          resolve();
        });
    });
  }

  /**
   * Batch animate multiple corner house movements
   */
  public static animateMultipleCornerMovements(
    movements: Array<{
      sprite: PlanetSprite;
      startPosition: { x: number, y: number };
      endPosition: { x: number, y: number };
      startLongitude: number;
      endLongitude: number;
    }>,
    config: AnimationConfig = D3PlanetAnimator.ANIMATION_PRESETS.SMOOTH
  ): Promise<void[]> {
    
    const animations = movements.map(movement => 
      D3CornerHouseAnimator.animateCornerHouseMovement(
        movement.sprite,
        movement.startPosition,
        movement.endPosition,
        movement.startLongitude,
        movement.endLongitude,
        config
      )
    );

    return Promise.all(animations);
  }

  /**
   * Create smooth arc transition between different corner houses
   * Handles cases where planet moves from one corner house to another
   */
  public static animateCrossCornerTransition(
    sprite: PlanetSprite,
    fromContainer: number,
    toContainer: number,
    startPosition: { x: number, y: number },
    endPosition: { x: number, y: number },
    startLongitude: number,
    endLongitude: number,
    config: AnimationConfig = D3PlanetAnimator.ANIMATION_PRESETS.SMOOTH
  ): Promise<void> {
    
    const fromIsCorner = D3CornerHouseAnimator.isCornerHouse(fromContainer);
    const toIsCorner = D3CornerHouseAnimator.isCornerHouse(toContainer);

    if (!fromIsCorner && !toIsCorner) {
      // Regular movement between non-corner houses
      return D3PlanetAnimator.animatePlanetSprite(sprite, endPosition, 0, config);
    }

    if (fromIsCorner && toIsCorner) {
      // Corner to corner transition - create intermediate waypoints
      const midPoint = {
        x: (startPosition.x + endPosition.x) / 2,
        y: (startPosition.y + endPosition.y) / 2
      };

      // Calculate intermediate rotation
      const midLongitude = (startLongitude + endLongitude) / 2;
      const midRotation = D3CornerHouseAnimator.calculateCornerRotation(toContainer, midLongitude);

      // Create multi-phase animation
      return D3PlanetAnimator.chainAnimations(sprite, [
        {
          position: midPoint,
          rotation: midRotation,
          config: { ...config, duration: config.duration / 2 }
        },
        {
          position: endPosition,
          rotation: D3CornerHouseAnimator.calculateCornerRotation(toContainer, endLongitude),
          config: { ...config, duration: config.duration / 2 }
        }
      ]);
    }

    if (fromIsCorner && !toIsCorner) {
      // Corner to straight house - gradually reduce rotation
      const interpolator = d3.interpolateNumber(
        D3CornerHouseAnimator.calculateCornerRotation(fromContainer, startLongitude),
        0
      );

      return new Promise((resolve) => {
        sprite.container
          .transition()
          .duration(config.duration)
          .ease(config.easing)
          .tween('cornerToStraight', () => {
            const positionInterpolator = d3.interpolate(startPosition, endPosition);
            
            return (t: number) => {
              const position = positionInterpolator(t);
              const rotation = interpolator(t);
              sprite.container.attr('transform', `translate(${position.x}, ${position.y}) rotate(${rotation})`);
            };
          })
          .on('end', () => {
            sprite.position = endPosition;
            sprite.rotation = 0;
            sprite.containerIndex = toContainer;
            resolve();
          });
      });
    }

    if (!fromIsCorner && toIsCorner) {
      // Straight to corner house - gradually add rotation
      const interpolator = d3.interpolateNumber(
        0,
        D3CornerHouseAnimator.calculateCornerRotation(toContainer, endLongitude)
      );

      return new Promise((resolve) => {
        sprite.container
          .transition()
          .duration(config.duration)
          .ease(config.easing)
          .tween('straightToCorner', () => {
            const positionInterpolator = d3.interpolate(startPosition, endPosition);
            
            return (t: number) => {
              const position = positionInterpolator(t);
              const rotation = interpolator(t);
              sprite.container.attr('transform', `translate(${position.x}, ${position.y}) rotate(${rotation})`);
            };
          })
          .on('end', () => {
            sprite.position = endPosition;
            sprite.rotation = D3CornerHouseAnimator.calculateCornerRotation(toContainer, endLongitude);
            sprite.containerIndex = toContainer;
            resolve();
          });
      });
    }

    // Should never reach here, but fallback to regular animation
    return D3PlanetAnimator.animatePlanetSprite(sprite, endPosition, 0, config);
  }

  /**
   * Get corner house configuration for debugging
   */
  public static getCornerConfig(containerIndex: number): CornerHouseConfig | null {
    return D3CornerHouseAnimator.CORNER_CONFIGS[containerIndex] || null;
  }

  /**
   * Get all corner house container indices
   */
  public static getCornerHouseIndices(): number[] {
    return Object.keys(D3CornerHouseAnimator.CORNER_CONFIGS).map(Number);
  }

  /**
   * Calculate optimal easing function for corner house animations
   * Different easing for different corner positions
   */
  public static getCornerEasing(containerIndex: number): (t: number) => number {
    const easingMap: Record<number, (t: number) => number> = {
      0: d3.easeCubicOut,    // 12th house - smooth exit
      3: d3.easeQuadInOut,   // 3rd house - balanced
      6: d3.easeCubicIn,     // 6th house - smooth entry
      9: d3.easeElastic      // 9th house - bouncy (for visual interest)
    };

    return easingMap[containerIndex] || d3.easeCubicInOut;
  }

}